import React from 'react';
import { Tooltip } from 'react-tooltip';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/20/solid';
import Price from '@app/model/market_state/Price';
import Utilities from '@util/Utilities';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import MarketStateService from '@app/model/market_state/service/MarketStateService';

export interface MarketStateItemUrlReference {
    marketStateItemId: number;
    marketStateIds: number[];
}

export interface PerRoomAvailability {
    roomId: number | string;
    roomName: string;
    availability: number;
    price?: Price;
    urlReference?: MarketStateItemUrlReference;
}

export interface AvailabilitySnapshot {
    identity: number;
    creationDate: string;
    perRoomAvailability: PerRoomAvailability[];
}

interface Props {
    tooltipId: string;
    snapshots: AvailabilitySnapshot[];
    children: React.ReactNode;
}

const AvailabilityHistoryTooltip: React.FC<Props> = ({
                                                         tooltipId,
                                                         snapshots,
                                                         children
                                                     }) => {

    const {companyHotel} = useSelectedCompanyHotel();

    // Sort snapshots descending by creationDate
    const sorted = [...snapshots].sort(
        (a, b) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime()
    );

    // Build a list of changes by comparing each snapshot (i) to (i-1)
    const changes: Array<{
        creationDate: string;
        rows: Array<{ roomName: string; diff: number; prevPrice: Price | undefined; urlReference: MarketStateItemUrlReference | undefined }>;
    }> = [];

    for (let i = 1; i < sorted.length; i++) {
        const current = sorted[i - 1];
        const previous = sorted[i];

        const currentRooms = current.perRoomAvailability || [];
        const prevRooms = previous.perRoomAvailability || [];

        // union of room IDs from both snapshots
        const roomIds = new Set<number | string>([
            ...currentRooms.map(r => r.roomId),
            ...prevRooms.map(r => r.roomId),
        ]);

        const changedRooms: Array<{ roomName: string; diff: number; prevPrice: Price | undefined; urlReference: MarketStateItemUrlReference | undefined }> = [];

        roomIds.forEach(roomId => {
            const curr = currentRooms.find(r => r.roomId === roomId);
            const prev = prevRooms.find(r => r.roomId === roomId);

            const currAvail = curr ? curr.availability : 0;
            const prevAvail = prev ? prev.availability : 0;
            const diff = currAvail - prevAvail;

            if (diff !== 0) {
                // We want the previous day’s price in the last column
                const prevPrice = diff < 0 && prev ? prev.price : undefined;
                // Room name: prefer current, else previous
                const roomName = curr ? curr.roomName : (prev ? prev.roomName : "Unknown");
                changedRooms.push({ roomName, diff, prevPrice, urlReference: curr?.urlReference });
            }
        });

        if (changedRooms.length > 0) {
            changes.push({ creationDate: current.creationDate, rows: changedRooms });
        }
    }

    const latestTotal = sorted[0]?.perRoomAvailability.reduce((acc, room) => acc + room.availability, 0) ?? 0;
    const earliestTotal = sorted[sorted.length - 1]?.perRoomAvailability.reduce((acc, room) => acc + room.availability, 0) ?? 0;
    const totalDifference = latestTotal - earliestTotal;

    const renderDifference = (difference: number, showComparisonLabel = false) => {
        if (difference === 0) {
            return showComparisonLabel ? <span className="text-xs text-gray-400">0 vs {companyHotel.settings.calendar.numberOfDaysForPriceParity}d</span> : null;
        }

        const Icon = difference > 0 ? ArrowUpIcon : ArrowDownIcon;
        const colorClassName = difference > 0 ? 'text-rose-500' : 'text-emerald-500';

        return <span className={`inline-flex items-center gap-x-0.5 text-xs font-medium ${colorClassName}`}>
            <Icon className="h-3 w-3" />
            <span>{Math.abs(difference)}</span>
            {showComparisonLabel && <span className="ml-0.5 text-[10px] font-medium text-slate-400">vs {companyHotel.settings.calendar.numberOfDaysForPriceParity}d</span>}
        </span>;
    };

    const renderPrice = (price: Price | undefined) => {
        if (!price || !price.currency || price.amount === 0) return '-';
        return Utilities.formatPrice(price);
    };

    return (
        <>
            {/* Trigger element with a unique data-tooltip-id */}
            <span className="inline-flex" data-tooltip-id={tooltipId}>{children}</span>

            <Tooltip
                id={tooltipId}
                place="top"
                className="z-50 !rounded-lg !bg-white !p-0 !opacity-100 shadow-xl ring-1 ring-black ring-opacity-5"
                style={{ backgroundColor: "white" }}
                clickable
                openOnClick={true}
            >
                {changes.length === 0 ? (
                    <div className="w-72 rounded-lg bg-white p-3 text-xs text-slate-500">
                        No changes found.
                    </div>
                ) : (
                    <div className="w-80 overflow-hidden rounded-lg bg-white text-xs text-slate-500">
                        <div className="flex items-center justify-between gap-x-4 px-4 py-3">
                            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                                Last {companyHotel.settings.calendar.numberOfDaysForPriceParity} days evolution
                            </div>
                            {renderDifference(totalDifference, true)}
                        </div>
                        <div className="max-h-64 overflow-y-auto border-t border-slate-100 px-4 py-3">
                            <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-slate-400">By room</div>
                            <div className="space-y-3">
                                {changes.map(day => (
                                    <div key={day.creationDate}>
                                        <div className="mb-1 text-[10px] font-medium text-slate-400">
                                            {Utilities.formatDateString(day.creationDate)}
                                        </div>
                                        <div className="space-y-2">
                                            {day.rows.map((row, idx) => (
                                                <div key={`${day.creationDate}-${idx}`} className="grid grid-cols-[minmax(0,1fr)_2.5rem_3.75rem] items-center gap-x-3">
                                                    <span className="min-w-0 truncate text-xs font-medium text-slate-600">
                                                        {renderRoomName(row.roomName, row.urlReference)}
                                                    </span>
                                                    <span className="justify-self-end">{renderDifference(row.diff)}</span>
                                                    <span className="justify-self-end text-right text-xs font-semibold text-slate-900">
                                                        {renderPrice(row.prevPrice)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-[10px] font-medium text-slate-400">
                            Availability history · {changes.length} {changes.length === 1 ? 'snapshot' : 'snapshots'} with changes
                        </div>
                    </div>
                )}
            </Tooltip>
        </>
    );
};

const openMarketStateItemUrl = (reference: MarketStateItemUrlReference) => {
    MarketStateService.constructMarketStateItemUrl(reference.marketStateItemId, reference.marketStateIds)
        .then(url => {
            if (url !== null && url !== undefined && url !== '') window.open(url, '_blank', 'noopener,noreferrer');
        });
};

const renderRoomName = (roomName: string, reference: MarketStateItemUrlReference | undefined) => {
    if (reference === undefined) return roomName;
    return <button type="button" className="hover:underline" onClick={() => openMarketStateItemUrl(reference)}>{roomName}</button>;
};

export default AvailabilityHistoryTooltip;
